import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardCheck, Droplets, Search, ShieldCheck, Users, Wrench } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { adminAPI, profitSharingAPI, stewardAPI, unitAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { hasAdminPermission } from '../utils/adminPermissions';
import { normalizeOwnedUserRoles } from '../utils/userRoles';
import {
  calculateComplianceRate,
  formatCurrency,
  formatNumber,
  getDaysElapsedInMonth,
  getUserDisplayName,
  getUserPhone,
  isSameMonth
} from '../utils/operations';

function MetricCard({ icon: Icon, label, value, accent = 'cyan' }) {
  const accentStyles = {
    cyan: 'border-cyan-100 bg-cyan-50 text-cyan-700',
    emerald: 'border-emerald-100 bg-emerald-50 text-emerald-700',
    amber: 'border-amber-100 bg-amber-50 text-amber-700',
    purple: 'border-purple-100 bg-purple-50 text-purple-700'
  };

  return (
    <div className={`rounded-lg border p-5 ${accentStyles[accent] || accentStyles.cyan}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-[0.24em]">{label}</span>
        <Icon size={18} />
      </div>
      <div className="mt-4 text-3xl font-black text-[var(--admin-text-primary)]">{value}</div>
    </div>
  );
}

function getRoleBadge(role, isActive = false) {
  const styles = {
    'Super-Admin': 'bg-red-500/10 text-red-400 border-red-500/20',
    Admin: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
    GM: 'bg-cyan-400/10 text-cyan-400 border-cyan-400/20',
    Finance: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
    Business: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
    AfterSales: 'bg-indigo-500/10 text-indigo-500 border-indigo-500/20',
    RP: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
    Steward: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
    User: 'bg-slate-500/10 text-slate-400 border-slate-500/20'
  };

  return `px-2 py-0.5 rounded-lg text-[10px] font-bold border ${styles[role] || 'bg-slate-500/10 text-slate-500 border-slate-500/20'} ${isActive ? 'ring-2 ring-offset-2 ring-offset-white ring-cyan-400' : ''}`;
}

export default function StewardManagementTable() {
  const { t } = useTranslation();
  const currentAdmin = useAuthStore((state) => state.user);
  const canViewPartnerNetwork = hasAdminPermission(currentAdmin, 'view_partner_network');
  const canReviewCheckins = hasAdminPermission(currentAdmin, 'review_steward_checkins');
  const [stewards, setStewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const formatDisplayDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('en-PH', { timeZone: 'Asia/Manila' });
  };

  const translateRole = (role) => t(`userManagement.roles.${role}`, { defaultValue: role });

  useEffect(() => {
    loadData();
    // The list performs one complete initial load; user actions do not mutate it in place.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');

    try {
      const [usersRes, unitsRes, treeRes, summaryRes, historyRes, complianceRes] = await Promise.all([
        adminAPI.getUsersAllPages({ role: 'Steward' }),
        unitAPI.getUnits(),
        adminAPI.getPartnerTree(),
        canViewPartnerNetwork
          ? profitSharingAPI.getRoleSummary({ role: 'Steward' })
          : Promise.resolve({ data: { data: { roles: [] } } }),
        canReviewCheckins ? stewardAPI.getCheckInHistory({ limit: 500 }) : Promise.resolve({ data: { data: [] } }),
        canViewPartnerNetwork ? stewardAPI.getComplianceSummary() : Promise.resolve({ data: { data: { stewards: [] } } })
      ]);

      const users = usersRes.data?.data || [];
      const units = unitsRes.data?.data || [];
      const partnerTree = treeRes.data?.data || [];
      const roleSummary = summaryRes.data?.data?.roles || [];
      const history = historyRes.data?.data || [];
      const complianceByStewardId = Object.fromEntries(
        (complianceRes.data?.data?.stewards || []).map((entry) => [String(entry.stewardId), entry])
      );
      const daysElapsed = getDaysElapsedInMonth();

      const rpByStewardId = {};
      partnerTree.forEach((rp) => {
        (rp.children || []).forEach((steward) => {
          rpByStewardId[String(steward.id)] = rp;
        });
      });

      const summaryByUserId = {};
      roleSummary.forEach((entry) => {
        summaryByUserId[String(entry.userId)] = entry;
      });

      const records = users.map((user) => {
        const userId = String(user.id);
        const managedUnits = units.filter((unit) => String(unit.stewardId) === userId);
        const monthlyCheckins = history.filter(
          (record) => String(record.stewardId) === userId && isSameMonth(record.createdAt)
        );
        const summary = summaryByUserId[userId] || {};
        const compliance = complianceByStewardId[userId] || {};
        const lastCheckIn = [...monthlyCheckins].sort(
          (left, right) => new Date(right.createdAt) - new Date(left.createdAt)
        )[0];

        return {
          ...user,
          rp: rpByStewardId[userId] || null,
          managedUnits,
          monthlyCheckins,
          summary,
          complianceRate: calculateComplianceRate({
            completedCheckins: Number(compliance.completedCheckins ?? monthlyCheckins.length),
            unitCount: managedUnits.length,
            daysElapsed
          }),
          lastCheckInAt: compliance.lastCheckInAt || lastCheckIn?.createdAt || null
        };
      });

      setStewards(records);
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('stewardManagement.errors.load'));
    } finally {
      setLoading(false);
    }
  };

  const filteredStewards = stewards.filter((steward) => {
    const search = searchTerm.trim().toLowerCase();
    if (!search) return true;

    const roles = normalizeOwnedUserRoles(steward);
    return (
      getUserDisplayName(steward).toLowerCase().includes(search) ||
      getUserPhone(steward).includes(search) ||
      roles.some((role) => String(role).toLowerCase().includes(search)) ||
      steward.managedUnits.some((unit) => String(unit.deviceId || '').toLowerCase().includes(search))
    );
  });

  const totalManagedUnits = stewards.reduce((sum, steward) => sum + steward.managedUnits.length, 0);
  const totalProfit = stewards.reduce((sum, steward) => sum + Number(steward.summary?.totalProfit || 0), 0);
  const avgCompliance = stewards.length
    ? Math.round(stewards.reduce((sum, steward) => sum + steward.complianceRate, 0) / stewards.length)
    : 0;

  return (
    <div className="space-y-6 p-4 text-[var(--admin-text-primary)] sm:p-6 lg:p-8">
      <header className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h2 className="flex items-center gap-3 text-3xl font-black">
            <Wrench className="text-[var(--admin-accent)]" size={30} />
            {t('stewardManagement.title')}
          </h2>
          <p className="mt-2 max-w-3xl text-sm text-[var(--admin-text-muted)]">
            {t('stewardManagement.subtitle')}
          </p>
        </div>

        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[var(--admin-text-muted)]" size={16} />
          <input
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder={t('stewardManagement.searchPlaceholder')}
            className="admin-input w-full rounded-lg py-3 pl-11 pr-4 text-sm outline-none transition"
          />
        </div>
      </header>

      {error && <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700">{error}</div>}

      <section className={`grid gap-4 md:grid-cols-2 ${canViewPartnerNetwork ? 'xl:grid-cols-4' : 'xl:grid-cols-3'}`}>
        <MetricCard icon={Users} label={t('stewardManagement.metrics.stewards')} value={formatNumber(stewards.length)} accent="cyan" />
        <MetricCard icon={Droplets} label={t('stewardManagement.metrics.managedUnits')} value={formatNumber(totalManagedUnits)} accent="emerald" />
        {canViewPartnerNetwork && (
          <MetricCard icon={ClipboardCheck} label={t('stewardManagement.metrics.monthProfit')} value={formatCurrency(totalProfit)} accent="amber" />
        )}
        <MetricCard icon={ShieldCheck} label={t('stewardManagement.metrics.avgCompliance')} value={`${avgCompliance}%`} accent="purple" />
      </section>

      <section className="space-y-4 lg:hidden">
        {loading && (
          <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center text-slate-500">{t('stewardManagement.loading')}</div>
        )}

        {!loading && filteredStewards.length === 0 && (
          <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center text-slate-500">{t('stewardManagement.empty')}</div>
        )}

        {!loading && filteredStewards.map((steward) => {
          const roles = normalizeOwnedUserRoles(steward);

          return (
            <article key={`mobile-${steward.id}`} className="rounded-3xl border border-slate-200 bg-white p-5 text-slate-950 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-cyan-100 font-black text-cyan-700 uppercase">
                  {getUserDisplayName(steward).slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-slate-500">ID: {steward.id}</p>
                  <Link to={`/stewards/${steward.id}`} className="block truncate text-lg font-black text-slate-950">
                    {getUserDisplayName(steward)}
                  </Link>
                  <p className="text-sm text-slate-600">{getUserPhone(steward)}</p>
                </div>
                <Link
                  to={`/stewards/${steward.id}`}
                  className="shrink-0 rounded-2xl bg-cyan-500 px-3 py-2 text-sm font-black text-white"
                >
                  {t('stewardManagement.viewDetail')}
                </Link>
              </div>

              <div className="mt-4 flex flex-wrap gap-1">
                {roles.map((role) => (
                  <span key={`mobile-${steward.id}-${role}`} className={getRoleBadge(role, steward.activeRole === role)}>
                    {translateRole(role)}
                  </span>
                ))}
              </div>

              <div className="mt-4 grid gap-3 text-sm">
                <div className="rounded-2xl bg-slate-50 p-3">
                  <p className="font-bold text-slate-500">{t('stewardManagement.table.rp', { defaultValue: 'Partner PA' })}</p>
                  <p className="mt-1 font-black text-slate-950">{steward.rp?.name || t('stewardManagement.rp.unassigned')}</p>
                  <p className="text-slate-600">{steward.rp?.phoneNumber || t('stewardManagement.rp.notBound')}</p>
                </div>

                <div className="rounded-2xl bg-slate-50 p-3">
                  <p className="font-bold text-slate-500">{t('stewardManagement.table.devices')}</p>
                  <p className="mt-1 font-black text-slate-950">{t('stewardManagement.devicesCount', { count: formatNumber(steward.managedUnits.length) })}</p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {steward.managedUnits.slice(0, 3).map((unit) => (
                      <Link
                        key={`mobile-unit-${steward.id}-${unit.id}`}
                        to={`/units/${unit.id}`}
                        className="rounded-full bg-slate-200 px-2 py-1 text-xs font-semibold text-slate-700"
                      >
                        {unit.deviceId}
                      </Link>
                    ))}
                  </div>
                </div>

                {(canReviewCheckins || canViewPartnerNetwork) && <div className={`grid gap-3 ${canReviewCheckins && canViewPartnerNetwork ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  {canReviewCheckins && <div className="rounded-2xl bg-slate-50 p-3">
                    <p className="font-bold text-slate-500">{t('stewardManagement.table.checkins')}</p>
                    <p className="mt-1 font-black text-slate-950">{t('stewardManagement.thisMonthCount', { count: formatNumber(steward.monthlyCheckins.length) })}</p>
                  </div>}
                  {canViewPartnerNetwork && <div className="rounded-2xl bg-slate-50 p-3">
                    <p className="font-bold text-slate-500">{t('stewardManagement.table.compliance')}</p>
                    <p className="mt-1 font-black text-emerald-700">{steward.complianceRate}%</p>
                    <p className="text-slate-600">{formatDisplayDateTime(steward.lastCheckInAt)}</p>
                  </div>}
                </div>}
              </div>
            </article>
          );
        })}
      </section>

      <section className="admin-card hidden overflow-x-auto rounded-lg lg:block">
        <table className="w-full text-left">
          <thead className="admin-muted-panel text-[10px] uppercase tracking-widest text-[var(--admin-text-muted)] font-black">
            <tr>
              <th className="p-6">{t('stewardManagement.table.user')}</th>
              <th className="p-6">{t('stewardManagement.table.roles')}</th>
              <th className="p-6">{t('stewardManagement.table.rp')}</th>
              <th className="p-6">{t('stewardManagement.table.devices')}</th>
              {canReviewCheckins && <th className="p-6">{t('stewardManagement.table.checkins')}</th>}
              {canViewPartnerNetwork && <th className="p-6">{t('stewardManagement.table.compliance')}</th>}
              <th className="sticky right-0 z-10 bg-[var(--admin-surface)] p-6 text-right shadow-[-12px_0_18px_rgba(15,23,42,0.08)]">{t('stewardManagement.table.actions')}</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {loading && (
              <tr>
                <td colSpan={5 + (canReviewCheckins ? 1 : 0) + (canViewPartnerNetwork ? 1 : 0)} className="p-12 text-center text-[var(--admin-text-muted)]">{t('stewardManagement.loading')}</td>
              </tr>
            )}

            {!loading && filteredStewards.length === 0 && (
              <tr>
                <td colSpan={5 + (canReviewCheckins ? 1 : 0) + (canViewPartnerNetwork ? 1 : 0)} className="p-12 text-center text-[var(--admin-text-muted)]">{t('stewardManagement.empty')}</td>
              </tr>
            )}

            {!loading && filteredStewards.map((steward) => {
              const roles = normalizeOwnedUserRoles(steward);

              return (
                <tr key={steward.id} className="border-b border-[var(--admin-border)] transition-all hover:bg-[var(--admin-surface-soft)]">
                  <td className="min-w-[260px] p-6">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-cyan-50 font-black text-cyan-700 uppercase">
                        {getUserDisplayName(steward).slice(0, 1).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-xs text-[var(--admin-text-muted)] font-mono">ID: {steward.id}</p>
                        <Link
                          to={`/stewards/${steward.id}`}
                          className="font-bold text-[var(--admin-text-primary)] transition-colors hover:text-[var(--admin-accent)] hover:underline"
                        >
                          {getUserDisplayName(steward)}
                        </Link>
                        <p className="text-xs text-[var(--admin-text-muted)]">{getUserPhone(steward)}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-6">
                    <div className="flex flex-wrap gap-1">
                      {roles.map((role) => (
                        <span key={`${steward.id}-${role}`} className={getRoleBadge(role, steward.activeRole === role)}>
                          {translateRole(role)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="p-6">
                    <div className="text-sm font-bold text-[var(--admin-text-primary)]">{steward.rp?.name || t('stewardManagement.rp.unassigned')}</div>
                    <div className="text-xs text-[var(--admin-text-muted)]">{steward.rp?.phoneNumber || t('stewardManagement.rp.notBound')}</div>
                  </td>
                  <td className="p-6">
                    <div className="font-bold text-[var(--admin-text-primary)]">{t('stewardManagement.devicesCount', { count: formatNumber(steward.managedUnits.length) })}</div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {steward.managedUnits.slice(0, 3).map((unit) => (
                        <Link
                          key={unit.id}
                          to={`/units/${unit.id}`}
                          className="rounded-full border border-[var(--admin-border)] bg-[var(--admin-surface-soft)] px-2 py-1 text-[10px] font-bold text-[var(--admin-text-muted)] transition hover:border-cyan-300/60 hover:text-cyan-700"
                        >
                          {unit.deviceId}
                        </Link>
                      ))}
                      {steward.managedUnits.length > 3 && (
                        <span className="rounded-full border border-[var(--admin-border)] bg-[var(--admin-surface-soft)] px-2 py-1 text-[10px] text-[var(--admin-text-muted)]">
                          +{steward.managedUnits.length - 3}
                        </span>
                      )}
                    </div>
                  </td>
                  {canViewPartnerNetwork && <td className="p-6">
                    <div className="font-bold text-[var(--admin-text-primary)]">{t('stewardManagement.thisMonthCount', { count: formatNumber(steward.monthlyCheckins.length) })}</div>
                  </td>}
                  {canReviewCheckins && <td className="p-6">
                    <div className="font-bold text-emerald-700">{steward.complianceRate}%</div>
                    <div className="text-xs text-[var(--admin-text-muted)]">{formatDisplayDateTime(steward.lastCheckInAt)}</div>
                  </td>}
                  <td className="sticky right-0 bg-[var(--admin-surface)] p-6 text-right shadow-[-12px_0_18px_rgba(15,23,42,0.08)]">
                    <div className="flex justify-end">
                      <Link
                        to={`/stewards/${steward.id}`}
                        className="admin-secondary-button inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-bold transition"
                      >
                        {t('stewardManagement.viewDetail')}
                      </Link>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
}
